import { useState } from 'react';
import { Text, Button, Switch, Loader } from '@ui-component-library';
import { updateUserHubspotSubscriptions, updateDfrWeeklyEmails } from '@services/user';
import { useUserSettings } from './UserSettingsContext';
import { HubspotSubscription, HubspotSubscriptionDefinition, EmailSettingsSaveResult } from './components/Settings.types';

export default function EmailSettings() {
  const { userData, hubspotData, refetchHubspotData: onHubspotDataRefresh, refetchUserData: onUserDataRefresh } = useUserSettings();
    const [hubspotSubscriptions, setHubspotSubscriptions] = useState<Record<string, boolean>>({});
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    // Pending edit: null until user toggles, so displayed value stays in sync with async userData
    const [isDfrPendingEdit, setIsDfrPendingEdit] = useState<boolean | null>(null);
    const dfrWeeklyEmails = isDfrPendingEdit !== null ? isDfrPendingEdit : Boolean(userData?.dfr_weekly_emails_subscribed);
    const hasDfrChanges = isDfrPendingEdit !== null && isDfrPendingEdit !== Boolean(userData?.dfr_weekly_emails_subscribed);

    const definitions = (Array.isArray(hubspotData?.hubspot_subscription_definitions)
        ? hubspotData.hubspot_subscription_definitions
        : []) as HubspotSubscriptionDefinition[];


    const isLoading = hubspotData === null || hubspotData?.hubspot_subscription_definitions === undefined;
    const nameToId = Object.fromEntries(
        definitions.map((def) => [def.name_sv, def.subscription_id])
    );

    const getSubscriptionInfo = (nameSv: string) => {
        const subs = (hubspotData?.hubspot_user_subscriptions as HubspotSubscription[]) ?? [];
        const subId = nameToId[nameSv];
        const currentSub = subs.find((s) => s.subscription_id === subId);
        const isSubscribed = currentSub?.subscription_status === 'SUBSCRIBED' || currentSub?.subscription_status === 'NOT_SPECIFIED' || subs.length == 0;
        return { subId, isSubscribed };
    };

    const hasHubspotChanges = () =>
        definitions.some((def) => {
            const { isSubscribed } = getSubscriptionInfo(def.name_sv);
            return def.name_sv in hubspotSubscriptions && hubspotSubscriptions[def.name_sv] !== isSubscribed;
        });

    const handleSubmit = async () => {
        const toSubmit: { subscription_id: string; subscribed: boolean }[] = [];
        const promises = [];

        definitions.forEach((def) => {
            const { subId, isSubscribed } = getSubscriptionInfo(def.name_sv);
            if (def.name_sv in hubspotSubscriptions && hubspotSubscriptions[def.name_sv] !== isSubscribed && subId) {
                toSubmit.push({ subscription_id: subId, subscribed: hubspotSubscriptions[def.name_sv] });
            }
        });

        const updateHubspot = async (): Promise<EmailSettingsSaveResult | null> => {
            if (toSubmit.length === 0) return null;
            try {
                const res = await updateUserHubspotSubscriptions({ hubspot_subscriptions: toSubmit });
                return { type: 'hubspot', ok: res.ok };
            } catch (error) {
                console.error("Hubspot subscriptions update failed:", error);
                return { type: 'hubspot', ok: false, error };
            }
        }

        const updateDfr = async (): Promise<EmailSettingsSaveResult | null> => {
            if (!hasDfrChanges) return null;
            try {
                const res = await updateDfrWeeklyEmails({ dfr_weekly_emails: dfrWeeklyEmails });
                return { type: 'dfr', ok: res.ok };
            } catch (error) {
                console.error("DFR weekly emails update failed:", error);
                return { type: 'dfr', ok: false, error };
            }
        };

        const results = (await Promise.all([updateHubspot(), updateDfr()])).filter(
          (result): result is EmailSettingsSaveResult => result !== null
        );
        if (results.length === 0) return;

        const allSuccessful = results.every(res => res.ok);
        results.forEach(res => {
            if (res.type === 'hubspot' && res.ok) onHubspotDataRefresh();
            if (res.type === 'dfr' && res.ok) onUserDataRefresh?.();
        });

        if (allSuccessful) {
            setSuccess(true);
            setTimeout(() => setSuccess(false), 1000);
        } else {
            setError(true);
            setTimeout(() => setError(false), 2000);
        }
    }

    if (isLoading) {
        return (
            <div className="w-full max-w-[700px] mx-auto flex flex-col items-center justify-center py-16">
                <Loader />
                <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600 mt-4">
                    Laddar e-postinställningar...
                </Text>
            </div>
        );
    }

    return (
        <div className="w-full max-w-[700px] mx-auto">

            <section className=" p-4">
                <div className="p-6 border-b border-cloud-200 bg-cloud-50/30">
                    <Text variant="text-base/leading-normal/medium" className="text-charcoal-1000">
                        Dina e-postinställningar
                    </Text>
                    <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                        Hantera dina e-postinställningar.
                    </Text>
                </div>
                {success && (
                    <div className="rounded-lg bg-success-100 border border-success-300 px-4 py-3 mb-1 text-sm text-success-800">
                        Din information har sparats.
                    </div>
                )}
                {error && (
                    <div className="rounded-lg bg-danger-500 border border-danger-500 px-4 py-3 mb-1 text-sm text-danger-1000">
                        Ett fel uppstod när vi försökte spara dina inställningar. Vänligen försök igen.
                    </div>
                )}

                <div className="divide-y divide-cloud-200 space-y-2">
                    <div className="rounded-lg bg-white-50 p-6 flex items-center justify-between">
                        <div className="flex flex-col">
                            <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
                                Nödvändig e-post
                            </Text>
                            <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                                E-post som är kritiskt för plattformens funktioner.
                            </Text>
                        </div>
                        <Switch checked={true} disabled={true} onChange={() => { }} />
                    </div>
                    {definitions.filter(d=>d.isActive).map((subscription) => {
                        const { isSubscribed } = getSubscriptionInfo(subscription.name_sv);
                        const checked =
                            subscription.name_sv in hubspotSubscriptions
                                ? hubspotSubscriptions[subscription.name_sv]
                                : isSubscribed;

                        return (
                            <div
                                key={subscription.subscription_id}
                                className="rounded-lg bg-white-50 p-6 flex items-center justify-between"
                            >
                                <div className="flex flex-col">
                                    <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
                                        {subscription.name_sv}
                                    </Text>
                                    <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                                        {subscription.description}
                                    </Text>
                                </div>
                                <Switch
                                    checked={checked}
                                    onChange={(newChecked) => {
                                        setHubspotSubscriptions((prev) => ({
                                            ...prev,
                                            [subscription.name_sv]: newChecked,
                                        }));
                                    }}
                                />
                            </div>
                        );
                    })}
                    {userData?.is_plus &&<div className="rounded-lg bg-white-50 p-6 flex items-center justify-between">
                        <div className="flex flex-col">
                            <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
                                Veckovisa e-post om skogsövervakning
                            </Text>
                            <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                                Vi informerar dig veckovis.
                            </Text>
                        </div>
                        <Switch checked={dfrWeeklyEmails} onChange={(newChecked) => setIsDfrPendingEdit(newChecked)} />
                    </div>}
                </div>
            </section>
            <div className="flex justify-end px-4">
                <Button
                    tw
                    variant="primary"
                    onClick={() => handleSubmit()}
                    disabled={!hasHubspotChanges() && !hasDfrChanges}
                >
                    Spara uppgifter
                </Button>
            </div>
        </div>
    );
}